import { leasesPath } from '../../../Helpers/leaseUtils';

/**
 * Pure view logic for the category tabs on the building page (web #1488):
 * which tab the URL points at, what each tab's badge says and where the cards
 * lead. No Firebase, no React.
 */

// ---------------------------------------------------------------- tabs

export type CategoryView = 'allmant' | 'underhall' | 'ekonomi' | 'dokumentation' | 'risker';

/** `?tab=` belongs to Ritningar/Komponenter further down the page, so the category uses its own key. */
export const VIEW_PARAM = 'vy';

/** The page opens on Allmänt (web #1677). */
export const DEFAULT_VIEW: CategoryView = 'allmant';

export interface CategoryTab {
    key: CategoryView;
    label: string;
}

export const CATEGORY_TABS: CategoryTab[] = [
    { key: 'allmant', label: 'Allmänt' },
    { key: 'underhall', label: 'Underhåll & drift' },
    { key: 'ekonomi', label: 'Ekonomi & uthyrning' },
    { key: 'dokumentation', label: 'Dokumentation & historik' },
    { key: 'risker', label: 'Risker' },
];

export interface RiskCounts {
    hazards: number;
    riskConstructions: number;
    workEnvironment: number;
}

export const totalRisks = (risks: RiskCounts): number =>
    risks.hazards + risks.riskConstructions + risks.workEnvironment;

export interface CategoryTabSources {
    todoCount: number;
    hasPoorReception: boolean;
    risks: RiskCounts;
}

/** The tabs the building gets: Risker only while it has any risk to show. */
export const visibleTabs = (sources: CategoryTabSources): CategoryTab[] =>
    CATEGORY_TABS.filter(tab => tab.key !== 'risker' || totalRisks(sources.risks) > 0);

/** The tab the URL points at, among the visible ones; anything else opens the default. */
export const parseView = (value: string | null, tabs: CategoryTab[] = CATEGORY_TABS): CategoryView =>
    tabs.find(tab => tab.key === value)?.key ?? DEFAULT_VIEW;

/** The badge on a tab, only from data the page already has; null when there is nothing to flag. */
export const tabBadge = (view: CategoryView, sources: CategoryTabSources): string | null => {
    switch (view) {
        case 'underhall': return sources.todoCount > 0 ? String(sources.todoCount) : null;
        case 'allmant': return sources.hasPoorReception ? '!' : null;
        case 'risker': return totalRisks(sources.risks) > 0 ? String(totalRisks(sources.risks)) : null;
        default: return null;
    }
};

// ---------------------------------------------------------------- destinations

export const buildingPaths = (buildingKey: string) => ({
    maintenance: `/maintenanceActive/${buildingKey}`,
    checks: `/building/${buildingKey}/checks`,
    projects: `/building/${buildingKey}/projects`,
    todos: `/building/${buildingKey}/todos`,
    leases: leasesPath(buildingKey),
    tenants: `/tenants?building=${buildingKey}`,
    units: `/building/${buildingKey}/units`,
    parking: `/building/${buildingKey}/parking`,
    storageUnits: `/building/${buildingKey}/storage-units`,
    opCosts: `/building/${buildingKey}/operating-costs`,
    keys: `/keys?in=${buildingKey}`,
    images: `/building/${buildingKey}/images`,
    documents: `/building/${buildingKey}/documents`,
    timeline: `/building/${buildingKey}/timeline`,
    contacts: `/building/${buildingKey}/external-contacts`,
    gps: `/building/${buildingKey}/gps-map`,
    risks: `/building/${buildingKey}/risks`,
});
